import { createAuthClient } from "better-auth/react";
import {
  adminClient,
  organizationClient,
  twoFactorClient,
} from "better-auth/client/plugins";
import { i18n } from "@/i18n";
import type authStrings from "@/i18n/locales/en/auth";

/**
 * Auth client.
 *
 * The plugin list mirrors the server's — Better Auth derives the client's
 * methods from it, so a plugin missing here means the corresponding calls
 * silently don't exist rather than failing loudly.
 *
 * `AuthClient` is declared rather than inferred, for the same reason as the
 * server's `Auth` interface: Better Auth's real type reaches into its own
 * bundled zod (v4, against our v3) and TypeScript reports it as "not portable".
 * Naming the members we call keeps that contained and documents the surface —
 * swapping auth libraries means satisfying this, nothing more.
 */

type Result<T> = Promise<{ data: T | null; error: AuthError | null }>;
type AuthError = { code?: string; message?: string; status?: number };

type Organization = { id: string; name: string; slug: string };
type SessionUser = { id: string; email: string; name: string };

interface AuthClient {
  signIn: {
    email: (input: { email: string; password: string }) => Result<{
      twoFactorRedirect?: boolean;
    }>;
  };
  signOut: () => Promise<unknown>;
  resetPassword: (input: { newPassword: string; token: string }) => Result<unknown>;
  requestPasswordReset: (input: {
    email: string;
    redirectTo?: string;
  }) => Result<unknown>;
  getSession: (input?: {
    query?: { disableCookieCache?: boolean };
  }) => Promise<unknown>;
  useSession: () => { data: { user: SessionUser } | null; isPending: boolean };
  useListOrganizations: () => { data: Organization[] | null; isPending: boolean };
  useActiveOrganization: () => { data: Organization | null; isPending: boolean };
  organization: {
    setActive: (input: { organizationId: string }) => Promise<unknown>;
    inviteMember: (input: { email: string; role: string }) => Result<unknown>;
  };
  twoFactor: {
    verifyTotp: (input: { code: string }) => Result<unknown>;
    verifyBackupCode: (input: { code: string }) => Result<unknown>;
    enable: (input: { password: string }) => Result<{
      totpURI: string;
      backupCodes: string[];
    }>;
    disable: (input: { password: string }) => Result<unknown>;
  };
}

/**
 * Better Auth requires an absolute base URL — a relative `/api/auth` is
 * rejected at construction and leaves the whole app blank.
 *
 * In dev, Vite proxies `/api` to the API, so the current origin is correct.
 * In production `VITE_API_URL` is baked in at build time and points at the API
 * subdomain.
 */
const AUTH_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/auth`
  : `${window.location.origin}/api/auth`;

/**
 * Where Better Auth's own endpoints live.
 *
 * Exported because email verification is a plain GET on that handler rather
 * than a client method, so the verify page has to build the URL itself.
 */
export const authBaseUrl = AUTH_BASE_URL;

export const authClient = createAuthClient({
  baseURL: AUTH_BASE_URL,
  plugins: [organizationClient(), twoFactorClient(), adminClient()],
}) as unknown as AuthClient;

export const { signIn, signOut, resetPassword, organization, twoFactor } = authClient;

export const useSession = authClient.useSession;
/**
 * Organization hooks live on the client root, not under the `organization`
 * namespace — that namespace holds the imperative calls (`setActive`,
 * `inviteMember`), while the hooks are generated alongside `useSession`.
 */
export const useListOrganizations = authClient.useListOrganizations;
export const useActiveOrganization = authClient.useActiveOrganization;

/**
 * Human-readable messages for the error codes the UI actually surfaces.
 *
 * Better Auth's raw messages are accurate but written for developers. Anything
 * unmapped falls through to a generic line rather than leaking internals.
 *
 * Note that a wrong email and a wrong password give the same message on
 * purpose: distinguishing them turns the sign-in form into a way to find out
 * who has an account.
 */
const MESSAGES: Record<string, keyof typeof authStrings.errors> = {
  INVALID_EMAIL_OR_PASSWORD: "invalidCredentials",
  USER_NOT_FOUND: "invalidCredentials",
  INVALID_PASSWORD: "invalidPassword",
  INVALID_EMAIL: "invalidEmail",
  EMAIL_NOT_VERIFIED: "emailNotVerified",
  INVALID_TWO_FACTOR_CODE: "invalidCode",
  // What Better Auth's two-factor plugin actually sends for a wrong TOTP code.
  INVALID_CODE: "invalidCode",
  INVALID_BACKUP_CODE: "invalidBackupCode",
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: "tooManyAttempts",
  ACCOUNT_TEMPORARILY_LOCKED: "tooManyAttempts",
  // The short-lived cookie between the password and the code step has gone.
  INVALID_TWO_FACTOR_COOKIE: "twoFactorExpired",
  PASSWORD_TOO_SHORT: "passwordTooShort",
  PASSWORD_TOO_LONG: "passwordTooLong",
  INVALID_TOKEN: "invalidToken",
  TOKEN_EXPIRED: "invalidToken",
  USER_ALREADY_EXISTS: "userExists",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "userExists",
};

/**
 * Keys rather than sentences, translated when the error is shown — this module
 * loads before anyone has picked a language. Better Auth's rate limiter answers
 * 429 with no code at all, so that one is matched on status.
 */
export function authErrorMessage(error: AuthError | null): string | null {
  if (!error) return null;
  const key =
    (error.code ? MESSAGES[error.code] : undefined) ??
    (error.status === 429 ? "rateLimited" : "generic");
  return i18n.t(`auth:errors.${key}`);
}
